import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./AuraSosPanel.css";

export type AuraSosIncident = {
  incident_id: string;
  status?: string;
  severity?: string;
  created_at?: string;
  org_code?: string;
  student_pk?: string;
  student_id?: string;
  grade?: string;
  display_name?: string;
  handled_by_role?: string;
  handled_by_id?: string;
};

type Props = {
  role: "admin" | "school" | "doctor";
  orgCode?: string;
  responderId?: string | number;
  canAccept?: boolean;
  canClose?: boolean;
  compact?: boolean;
  title?: string;
};

const API_BASE = (
  process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000"
).replace(/\/+$/, "");

function getAccessToken(): string {
  return (
    localStorage.getItem("admin_token") ||
    sessionStorage.getItem("admin_token") ||
    localStorage.getItem("doctor_token") ||
    sessionStorage.getItem("doctor_token") ||
    localStorage.getItem("school_token") ||
    sessionStorage.getItem("school_token") ||
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("token") ||
    ""
  );
}

export default function AuraSosPanel({
  role,
  orgCode = "",
  responderId = "",
  canAccept = false,
  canClose = false,
  compact = false,
  title = "Active AURA SOS™ Incidents",
}: Props) {
  const [rows, setRows] = useState<AuraSosIncident[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [working, setWorking] = useState("");

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (orgCode) params.set("org_code", orgCode);
      const query = params.toString() ? `?${params.toString()}` : "";
      const token = getAccessToken();

      const response = await fetch(`${API_BASE}/realtime/sos/active${query}`, {
        cache: "no-store",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data?.ok === false) {
        throw new Error(data?.detail || `Request failed: ${response.status}`);
      }

      setRows(Array.isArray(data?.incidents) ? data.incidents : []);
      setError("");
    } catch (e: any) {
      setError(e?.message || "Unable to load AURA SOS incidents.");
    } finally {
      setLoading(false);
    }
  }, [orgCode]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 5000);
    return () => window.clearInterval(timer);
  }, [load]);

  const act = async (incidentId: string, action: "accept" | "close") => {
    try {
      setWorking(`${incidentId}:${action}`);
      const params = new URLSearchParams({
        role,
        responder_id: String(responderId || ""),
      });
      const token = getAccessToken();

      const response = await fetch(
        `${API_BASE}/realtime/sos/${encodeURIComponent(incidentId)}/${action}?${params}`,
        {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data?.ok === false) {
        throw new Error(data?.detail || `Request failed: ${response.status}`);
      }

      await load();
    } catch (e: any) {
      setError(e?.message || `Unable to ${action} incident.`);
    } finally {
      setWorking("");
    }
  };

  const active = useMemo(
    () => rows.filter((row) => String(row.status || "open").toLowerCase() !== "closed"),
    [rows]
  );

  if (!loading && !error && active.length === 0) {
    if (compact) return null;

    return (
      <section className="aura-sos-panel aura-sos-clear">
        <div className="aura-sos-head">
          <strong>🆘 {title}</strong>
          <span>0 active</span>
        </div>
        <div className="aura-sos-empty">No active AURA SOS™ incidents.</div>
      </section>
    );
  }

  return (
    <section
      className={`aura-sos-panel ${compact ? "compact" : ""}`}
      role="region"
      aria-label={title}
    >
      <div className="aura-sos-head">
        <strong>🆘 {title}</strong>
        <span>{active.length} active</span>
      </div>

      {error ? <div className="aura-sos-error">{error}</div> : null}
      {loading ? <div className="aura-sos-empty">Checking for active incidents…</div> : null}

      <div className="aura-sos-list">
        {active.map((row) => {
          const status = String(row.status || "open").toLowerCase();
          const studentName =
            row.display_name || row.student_id || `Student ${row.student_pk || ""}`.trim();

          return (
            <article key={row.incident_id} className={`aura-sos-item ${status}`}>
              <div className="aura-sos-item-main">
                <div className="aura-sos-incident">{row.incident_id}</div>
                <div className="aura-sos-student">{studentName}</div>
                <div className="aura-sos-meta">
                  School: {row.org_code || "Unknown"}
                  {row.grade ? ` • Grade ${row.grade}` : ""}
                  {" • "}
                  {row.created_at ? new Date(row.created_at).toLocaleString() : "Just now"}
                </div>
                <div className="aura-sos-status">
                  Status: {status.toUpperCase()}
                  {row.handled_by_id ? ` • Responder ${row.handled_by_id}` : ""}
                </div>
              </div>

              <div className="aura-sos-actions">
                {canAccept && status === "open" ? (
                  <button
                    type="button"
                    disabled={Boolean(working)}
                    onClick={() => act(row.incident_id, "accept")}
                  >
                    {working === `${row.incident_id}:accept`
                      ? "Accepting…"
                      : "Accept & Respond"}
                  </button>
                ) : null}

                {canClose ? (
                  <button
                    type="button"
                    className="secondary"
                    disabled={Boolean(working)}
                    onClick={() => act(row.incident_id, "close")}
                  >
                    {working === `${row.incident_id}:close`
                      ? "Closing…"
                      : "Close Incident"}
                  </button>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
